import React, { useState, useEffect } from 'react';
import { oneOfType, array, object, string } from 'prop-types';
import { get, map, isEmpty, flatten, size, split, join, includes, find, isNil } from 'lodash';
import moment from 'moment';
import { FlexContainer, HasPermissions, NotFound, Text, If, Loading, Button, Icon, Row, Pagination, Column, MobileWrapper, IsMobileWrapper, Modal } from '../../common';
import { Main } from '../../layouts/Wrapper/Base.components';
import { Mutation, Query } from '../../graphql';
import ShopperCard from './ShopperCard';
import { getSessionSalesChannel, removeNullFieldsFromVariables, thirdPartyCompanies } from '../../../utils/misc';
import PubSub from '../../../services/PubSub';
import MUTATION_EXPORT_CONTACTABILITY_REPORT from '../../graphql/exportContactabilityReport';
import DesktopFilters from './DesktopFilters';
import MobileFilters from './MobileFilters';
import { GET_SALES_CHANNELS, GET_USERS_BY_FILTER } from '../../../queries';
import DisconnectPicker from './DisconnectPicker';

const ModalBodyComponent = ({ icon, firstLine, secondLine }) => (
    <>
        <Icon name={icon} color='#1fa02e' size={80} bottom={20} />
        <Text text={firstLine} strong size={16} />
        { secondLine && <Text text={secondLine} /> }
    </>
);

ModalBodyComponent.propTypes = {
    icon: string,
    firstLine: string,
    secondLine: string
};

const ShoppersMonitor = ({ session }) => {
    const [showModal, setShowModal] = useState(false);
    const [showModalUserConnection, setShowModalUserConnection] = useState(false);
    const sessionSalesChannel = getSessionSalesChannel(session);
    const getSessionSalesChannelById = map(sessionSalesChannel, ({ value }) => value);
    const [hasntOnlyOneSalesChannel, setHasntOnlyOneSalesChannel] = useState(size(sessionSalesChannel) > 1);

    const rightHeader = () => {
        return (
            <HasPermissions to={['pickers-monitor:export']}>
                <FlexContainer direction='column'>
                    <FlexContainer direction='row'>
                        <Button width={100} text='Exportar' secondary onClick={() => setShowModal(true)} disabled={hasntOnlyOneSalesChannel} />
                    </FlexContainer>
                </FlexContainer>
            </HasPermissions>
        );
    };

    useEffect(() => {
        return () => {
            PubSub.dispatch('SET_RIGHT_HEADER', () => null);
        }
    }, [])

    useEffect(() => {
        PubSub.dispatch('SET_RIGHT_HEADER', () => rightHeader());
    }, [hasntOnlyOneSalesChannel]);

    const shoppersProfileId = sessionStorage.getItem('SHOPPERS_PROFILE_ID');

    const valueGetter = input => get(input, 'value', []);
    const valueMapper = input => map(input, ({ value }) => value);

    const dateFormatter = date => moment(date).format('YYYY/MM/DD');
    const today = dateFormatter(moment());

    const initialValues = {
        default: [],
        dateRangeStorage: { init: today, end: today },
        salesChannelStorage: size(sessionSalesChannel) === 1 ? sessionSalesChannel : [],
        companyStorage: map(thirdPartyCompanies({ nonCenco: true }), company => company)
    };

    const [dateRangeStorage, setDateRangeStorage] = useState(initialValues.dateRangeStorage);
    const [shopperStorage, setShopperStorage] = useState(initialValues.default);
    const [salesChannelStorage, setSalesChannelStorage] = useState(initialValues.salesChannelStorage);
    const [companyStorage, setCompanyStorage] = useState(initialValues.companyStorage);
    const [statusStorage, setStatusStorage] = useState(initialValues.default);
    const [shoppersInput, setShoppersInput] = useState(valueMapper(shopperStorage));
    const [companyGroupStorage, setCompanyGroup] = useState(initialValues.default);
    const [rut, setRut] = useState('');
    const [splitUser, setSplitUser] = useState('');

    // Pagination
    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(1);

    // Reset
    const [reseting, setReseting] = useState(false);

    // Disconnect picker hook's
    const [selectedPickerEmail, setSelectedPickerEmail] = useState(null);

    const constantFields = [
        {
            field: 'groups',
            value: [shoppersProfileId]
        },
        {
            field: 'status',
            value: isEmpty(statusStorage) ? [] : map(statusStorage, status => valueGetter(status))
        },
        {
            field: 'company',
            value: isEmpty(companyStorage) ? map(thirdPartyCompanies({ nonCenco: true }), company => valueGetter(company)) : map(companyStorage, company => valueGetter(company))
        }
    ];

    const [variables, setVariables] = useState({ dateRange: { init: today, end: today }, filters: constantFields, size: pageSize, page });

    const filters = [
        {
            field: 'email',
            value: valueMapper(shopperStorage)
        },
        {
            field: 'salesChannelInfo.salesChannelId',
            value: get(salesChannelStorage, 'value')
        },
        {
            field: 'splitUser',
            value: get(splitUser, 'value', '')
        },
        {
            field: 'rut',
            value: rut
        },
        ...constantFields
    ];

    const onSubmitFilters = (callback = () => {}, overwrite) => {
        callback();
        setHasntOnlyOneSalesChannel(false);
        setVariables({
            dateRange: dateRangeStorage,
            filters,
            size: pageSize,
            page,
            ...overwrite
        });
        setShoppersInput(valueMapper(shopperStorage));
    };

    const modalBodyResolver = (status) => {
        switch (status) {
            case '400':
                return { icon: 'notConnection', firstLine: 'Ups, algo falló', secondLine: 'Por favor vuelve a intentarlo' };
            case '200':
                    return { icon: 'check-mark', firstLine: 'Se exportaron los registros seleccionados', secondLine: 'Serán enviados dentro de unos minutos a tu correo' };
            default:
                return { icon: 'export', firstLine: '¿Está seguro de exportar los datos?' };
        };
    };

    const resetFilters = () => {
        setReseting(true);
        setDateRangeStorage(initialValues.dateRangeStorage);
        setCompanyGroup(initialValues.default);
        setSalesChannelStorage(initialValues.salesChannelStorage);
        setCompanyStorage(initialValues.companyStorage);
        setStatusStorage(initialValues.default);
        setShopperStorage(initialValues.default);
        setHasntOnlyOneSalesChannel(true);
        setRut('');
        setSplitUser('');
        setTimeout(() => {
            setReseting(false);
        }, 50);
    };

    const modal = () => {
        return (
            <Modal
                visible={showModal}
                width={400}
                destroyOnClose
                onClose={() => setShowModal(false)}
                withoutHeader
            >
                <Mutation mutation={MUTATION_EXPORT_CONTACTABILITY_REPORT}>
                    {({ data, loading, updateFn }) => {
                        const status = get(data, 'exportContactabilityReport.internalCode');
                        const successful = includes([status], '200');
                        return (
                            <>
                                <FlexContainer style={{ margin: '30px 0px' }} align='center' direction='column'>
                                    <ModalBodyComponent {...modalBodyResolver(status)} />
                                </FlexContainer>
                                <FlexContainer justify='center' align='center' style={{ width: '100%' }}>
                                    <Button
                                        link
                                        text={successful ? 'btn.action.close' : 'btn.action.cancel'}
                                        disabled={loading}
                                        style={{ width: '100px', margin: 0 }}
                                        onClick={() => setShowModal(false)}
                                    />
                                    { isNil(status) && <Button
                                        loading={loading}
                                        text={'btn.action.confirm'}
                                        style={{ width: '100px', margin: 0 }}
                                        disabled={loading}
                                        onClick={() => {
                                            const { init, end } = dateRangeStorage;
                                            if (!init || !end) return console.error('ERROR: init or end date hasnt defined');
                                            const mutationDateFormatter = field => {
                                                const date = join(split(get(dateRangeStorage, field), '/'), '-');
                                                if (includes(['init'], field)) return `${date} 00:00:00`;
                                                return `${date} 23:59:59`;
                                            };
                                            const store = get(find(sessionSalesChannel, ({ value }) => includes([get(salesChannelStorage, 'value')], value)), 'alternativeId');
                                            updateFn({
                                                variables: {
                                                    filters: {
                                                        store,
                                                        init: mutationDateFormatter('init'),
                                                        end: mutationDateFormatter('end'),
                                                        company: flatten([get(find(get(variables, 'filters', []), ({ field }) => includes(['company'], field)), 'value', [])]),
                                                        shoppers: size(JSON.parse(sessionStorage.getItem('shoppers'))) === size(shoppersInput) ? [] : shoppersInput
                                                    },
                                                    sendToExport: get(session, 'email')
                                                }
                                            });
                                        }}
                                    /> }
                                </FlexContainer>
                            </>
                        );
                    }}
                </Mutation>
            </Modal>
        );
    };

    const filterProperties = {
        userSalesChannel: getSessionSalesChannelById,
        sessionSalesChannel,
        onChangeDateRange: value => setDateRangeStorage({ init: dateFormatter(value[0]), end: dateFormatter(value[1]) }),
        onChangeShopper: value => setShopperStorage(valueGetter(value)),
        selectedShopper: shopperStorage,
        salesChannelDefaultValue: salesChannelStorage,
        onChangeSalesChannel: value => setSalesChannelStorage(valueGetter(value)),
        onChangeCompany: value => setCompanyStorage(valueGetter(value)),
        onChangeRut: ({ target }) => setRut(get(target, 'value', '')),
        rut,
        onChangeSplitUser: value => setSplitUser(valueGetter(value)),
        selectedSplitUser: splitUser,
        selectedCompany: companyStorage,
        onChangeStatus: value => setStatusStorage(valueGetter(value)),
        selectedStatus: statusStorage,
        onChangeCompanyGroup: value => setCompanyGroup(valueGetter(value)),
        selectedCompanyGroup: companyGroupStorage,
        onSubmit: () => onSubmitFilters(),
        onReset: () => resetFilters(),
        reseting
    };

    return (
        <HasPermissions
            to={['pickers-monitor:view']}
            otherwise={<NotFound firstTextLine='Página no encontrada' style={{ minHeight: '90vh' }} />}
        >
            <Row noWrap>
                <MobileWrapper hide showDesktop>
                    <HasPermissions to={['pickers-monitor:filter&search']}>
                        <DesktopFilters {...filterProperties} />
                    </HasPermissions>
                </MobileWrapper>
                <Main background='f4f4f4' withFooter>
                    <DisconnectPicker
                        email={selectedPickerEmail}
                        onClose={(callback = () => {}) => {
                            // callback();
                            setSelectedPickerEmail(null);
                            setShowModalUserConnection(false);
                        }}
                        showModal={showModalUserConnection}
                    />
                    <MobileWrapper>
                        <HasPermissions to={['pickers-monitor:filter&search']}>
                            <MobileFilters {...filterProperties} />
                        </HasPermissions>
                    </MobileWrapper>
                    <FlexContainer direction='column' height='100%'>
                        <If
                            condition={!reseting}
                            then={
                                <Query query={GET_USERS_BY_FILTER} variables={removeNullFieldsFromVariables(variables)} skip={hasntOnlyOneSalesChannel} pollInterval={60000}>
                                    {({ data: getUsersByFilter, loading, networkStatus }) => {
                                        !loading && console.log({ getUsersByFilter })
                                        const salesChannelLabel = split(get(salesChannelStorage, 'label', ' - '), ' - ')[1];
                                        const { totalRecords, data } = get(getUsersByFilter, 'getUsersByFilter', {});
                                        const totalRecordsLabel = totalRecords <= 100
                                            ? ['Se encontraron\u00A0', totalRecords, '\u00A0registros para\u00A0', salesChannelLabel]
                                            : ['Mostrando\u00A0', 100, '\u00A0registros de\u00A0', totalRecords, '\u00A0para\u00A0', salesChannelLabel];
                                        if (hasntOnlyOneSalesChannel) {
                                            return (
                                                <NotFound
                                                    firstTextLine='Aún no se ha realizado una búsqueda, utiliza los filtros.'
                                                    icon='find'
                                                    iconBackgroundColor='#f4f4f4'
                                                    backgroundColor='#f4f4f4'
                                                    iconColor='#c0c0c0'
                                                    iconSize={85}
                                                    fontSize={20}
                                                />
                                            );
                                        }
                                        return (
                                            <If
                                                condition={networkStatus === 6 ? false : loading}
                                                then={<Loading loading />}
                                                else={
                                                    <>
                                                        <IsMobileWrapper>
                                                            {({ isMobile }) => (
                                                                <FlexContainer
                                                                    pX={16}
                                                                    backgroundColor='#FFFFFF'
                                                                    boxShadow
                                                                    zIndex={1}
                                                                    direction={isMobile ? 'column' : 'row'}
                                                                >
                                                                    <Column direction='row' align='center' justify={isMobile && 'flex-end'}>
                                                                        { !isEmpty(data) && map(totalRecordsLabel, (element, key) => (
                                                                            <Text key={key} text={element} strong={includes([1, 3], key)} color={key === 3 ? '#1fa02e' : null} />
                                                                        )) }
                                                                    </Column>
                                                                    <Column display={isMobile ? 'block !important' : 'flex'}>
                                                                        <Pagination
                                                                            pageSize={pageSize}
                                                                            total={totalRecords}
                                                                            onChangePageSize={({ value }) => {
                                                                                const gotValue = get(value, 'value', 100);
                                                                                onSubmitFilters(setPageSize(gotValue), { size: gotValue });
                                                                            }}
                                                                            onChange={value => onSubmitFilters(setPage(value), { page: value })}
                                                                            current={page}
                                                                        />
                                                                    </Column>
                                                                </FlexContainer>
                                                            )}

                                                        </IsMobileWrapper>
                                                        <Query query={GET_SALES_CHANNELS}>
                                                            {({ data: salesChannels, loading }) => (
                                                                <If
                                                                    condition={loading}
                                                                    then={<Loading loading />}
                                                                    else={
                                                                        <FlexContainer direction='column' maxHeight='calc(95vh - 140px)' minHeight='fit-content' overflowY overflowX margin={11}>
                                                                            { map(data, (shopper, key) => {
                                                                                const { name } = find(get(salesChannels, 'salesChannels', []), ({ id }) => parseInt(id) === get(shopper, 'salesChannelActive', null)) || { name: 'No Disponible' };
                                                                                return (
                                                                                    <FlexContainer
                                                                                        key={key}
                                                                                        padding={5}
                                                                                    >
                                                                                        <ShopperCard
                                                                                            {...shopper}
                                                                                            salesChannelActive={name}
                                                                                            disconnectPicker={() => {
                                                                                                setShowModalUserConnection(true);
                                                                                                setSelectedPickerEmail(get(shopper, 'email'));
                                                                                            }}
                                                                                            isModalActive={!!selectedPickerEmail}
                                                                                        />
                                                                                    </FlexContainer>
                                                                                );
                                                                            })}
                                                                        </FlexContainer>
                                                                    }
                                                                />
                                                            )}
                                                        </Query>
                                                    </>
                                                }
                                            />
                                        );
                                    }}
                                </Query>
                            }
                        />
                    </FlexContainer>
                    { modal() }
                </Main>
            </Row>
        </HasPermissions>
    );
};

ShoppersMonitor.propTypes = {
    session: oneOfType([array, object])
};

export default ShoppersMonitor;
